import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from "react";
import MinimizedDrawerContext, {
  getLocalStorageMinimizedDrawerValue,
} from "./MinimizedDrawerContext";
import { useMediaQuery, useTheme } from "@mui/material";
import { SidebarTabs } from "../constants";
import { JourneyRunContext } from "./JourneyRunContext";

const SIDEBAR_INIT_STATE = false;

interface TemplateSidebarContextType {
  isOpen: boolean;
  activeTab: SidebarTabs;
  openSidebar: (tab?: SidebarTabs) => void;
  closeSidebar: () => void;
  changeActiveTab: (tab: SidebarTabs) => void;
}

const TemplateSidebarContext = createContext<TemplateSidebarContextType>({
  isOpen: false,
  activeTab: SidebarTabs.PREVIEW,
  openSidebar: () => {},
  closeSidebar: () => {},
  changeActiveTab: () => {},
});

export const TemplateSidebarProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.down("md"));

  const { setMinimizedDrawer } = useContext(MinimizedDrawerContext);
  const { isRunning } = useContext(JourneyRunContext);

  //
  //
  // STATES

  const [isOpen, setIsOpen] = useState(SIDEBAR_INIT_STATE);
  const [activeTab, setActiveTab] = useState<SidebarTabs>(SidebarTabs.PREVIEW);

  //
  //
  // METHODS

  const openSidebar = (tab?: SidebarTabs) => {
    setIsOpen(true);
    setActiveTab(tab ?? activeTab);
  };

  const closeSidebar = () => {
    setIsOpen(false);
    setActiveTab(SidebarTabs.PREVIEW);
  };

  const changeActiveTab = (tab: SidebarTabs) => {
    setActiveTab(tab);
  };

  //
  //
  // ADDITIONAL LOGIC

  // Minimize main drawer if sidebar opens
  useEffect(() => {
    if (!isRunning) {
      setMinimizedDrawer(isOpen ? true : getLocalStorageMinimizedDrawerValue());
    }
  }, [isOpen, isRunning]);

  // Close sidebar for small screens
  useEffect(() => {
    isMd && closeSidebar();
  }, [isMd]);

  return (
    <TemplateSidebarContext.Provider
      value={{
        isOpen,
        activeTab,
        openSidebar,
        closeSidebar,
        changeActiveTab,
      }}
    >
      {children}
    </TemplateSidebarContext.Provider>
  );
};

export const useTemplateSidebar = (): TemplateSidebarContextType => {
  const context = useContext(TemplateSidebarContext);
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider");
  }
  return context;
};
